import type { Map as MapboxMap } from "mapbox-gl";
import type { KretsMemberLocationsResponse } from "../../api/kretsMembers";

const SOURCE_ID = "distributor-pins";
const LAYER_DOT_ID = "distributor-pins-dot";
const LAYER_ICON_ID = "distributor-pins-icon";
const LAYER_LABEL_ID = "distributor-pins-label";
const IMAGE_ID = "distributor-home-icon";

const addHouseIcon = (map: MapboxMap): void => {
    if (map.hasImage(IMAGE_ID)) return;

    const size = 32;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d")!;

    // Orange circle background
    ctx.beginPath();
    ctx.arc(16, 16, 15, 0, Math.PI * 2);
    ctx.fillStyle = "#f97316";
    ctx.fill();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2.5;
    ctx.stroke();

    // House shape in white
    ctx.fillStyle = "#ffffff";
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1;

    // Roof (triangle)
    ctx.beginPath();
    ctx.moveTo(16, 7);   // peak
    ctx.lineTo(7, 16);   // left
    ctx.lineTo(25, 16);  // right
    ctx.closePath();
    ctx.fill();

    // Body
    ctx.fillRect(9, 15, 14, 10);

    // Door (cut out in orange)
    ctx.fillStyle = "#f97316";
    ctx.fillRect(13, 19, 6, 6);

    const imageData = ctx.getImageData(0, 0, size, size);
    map.addImage(IMAGE_ID, { width: size, height: size, data: imageData.data });
};

const ensureLayer = (map: MapboxMap): void => {
    addHouseIcon(map);

    if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] }
        });
    }

    if (!map.getLayer(LAYER_ICON_ID)) {
        map.addLayer({
            id: LAYER_ICON_ID,
            type: "symbol",
            source: SOURCE_ID,
            layout: {
                "icon-image": IMAGE_ID,
                "icon-size": ["interpolate", ["linear"], ["zoom"], 8, 0.6, 12, 0.9, 16, 1.1],
                "icon-allow-overlap": true,
                "icon-anchor": "center"
            }
        });
    }

    if (!map.getLayer(LAYER_DOT_ID)) {
        map.addLayer({
            id: LAYER_DOT_ID,
            type: "circle",
            source: SOURCE_ID,
            paint: {
                "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 4, 12, 6, 16, 8],
                "circle-color": "#f97316",
                "circle-stroke-color": "#ffffff",
                "circle-stroke-width": 2,
                "circle-opacity": 0.95
            }
        });
    }

    if (!map.getLayer(LAYER_LABEL_ID)) {
        map.addLayer({
            id: LAYER_LABEL_ID,
            type: "symbol",
            source: SOURCE_ID,
            layout: {
                "text-field": ["coalesce", ["get", "assignmentName"], ["get", "name"]],
                "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
                "text-size": 11,
                "text-offset": [0, 1.6],
                "text-anchor": "top",
                "text-optional": true
            },
            paint: {
                "text-color": "#1c1917",
                "text-halo-color": "#ffffff",
                "text-halo-width": 1.5
            }
        });
    }
};

export const updateDistributorPinsLayer = (
    map: MapboxMap,
    data: KretsMemberLocationsResponse | null | undefined,
    visible: boolean
): void => {
    if (!map.isStyleLoaded()) {
        map.once("style.load", () => updateDistributorPinsLayer(map, data, visible));
        return;
    }

    ensureLayer(map);

    const source = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    if (!source) return;

    const visibility = visible ? "visible" : "none";
    if (map.getLayer(LAYER_DOT_ID)) map.setLayoutProperty(LAYER_DOT_ID, "visibility", visibility);
    if (map.getLayer(LAYER_ICON_ID)) map.setLayoutProperty(LAYER_ICON_ID, "visibility", visibility);
    if (map.getLayer(LAYER_LABEL_ID)) map.setLayoutProperty(LAYER_LABEL_ID, "visibility", visibility);

    if (data) {
        source.setData(data as any);
    }

    // Keep distributor pins on top so they remain visible above polygon fills/lines.
    if (map.getLayer(LAYER_DOT_ID)) {
        map.moveLayer(LAYER_DOT_ID);
    }
    if (map.getLayer(LAYER_ICON_ID)) {
        map.moveLayer(LAYER_ICON_ID);
    }
    if (map.getLayer(LAYER_LABEL_ID)) {
        map.moveLayer(LAYER_LABEL_ID);
    }
};

export const removeDistributorPinsLayer = (map: MapboxMap): void => {
    if (map.getLayer(LAYER_LABEL_ID)) map.removeLayer(LAYER_LABEL_ID);
    if (map.getLayer(LAYER_ICON_ID)) map.removeLayer(LAYER_ICON_ID);
    if (map.getLayer(LAYER_DOT_ID)) map.removeLayer(LAYER_DOT_ID);
    if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
    if (map.hasImage(IMAGE_ID)) map.removeImage(IMAGE_ID);
};

export { LAYER_ICON_ID as DISTRIBUTOR_PINS_LAYER_ID };
